/*
 * Feature story — mobile "show more".
 *
 * Figma folds the body to seven lines on mobile (1317:12796) with a gradient
 * over the cut and a "show more" control sitting in it; 1378:17755 is the same
 * section opened, the control moved below the copy and reading "show less".
 * Desktop (1317:9064 and up) shows the whole body, so the fold is mobile only.
 *
 * Nothing here is configured. The element measures the body against seven of
 * its own lines and only takes over when the copy really is longer, so the
 * same section on the home page — three lines — never grows a button it does
 * not need, and no setting has to be kept in sync with the copy.
 *
 * The clamp is added by this script rather than rendered, which is what makes
 * the no-JS state safe: the body stands open and the button stays `hidden`, so
 * the copy is never shut behind a control that cannot open it.
 */

const FS_CLAMP_LINES = 7;
const FS_DESKTOP = '(min-width: 990px)';

class FeatureStoryBody extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.fs__body');
    this.button = this.querySelector('.fs__more');
    if (!this.body || !this.button) return;

    this.label = this.button.querySelector('.fs__more-label');
    this.expanded = false;
    this.desktop = window.matchMedia(FS_DESKTOP);

    this.button.addEventListener('click', () => this.toggle());
    this.desktop.addEventListener('change', () => this.update());

    // The measurement depends on the body's own line height and width, so it
    // has to be redone when either moves: a rotation, a resize, or a brand font
    // arriving after first paint and reflowing the copy. Observing the body
    // covers all three without listening for each separately.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.body);
    }

    this.update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /* Seven lines of whatever the body is currently set in — read off the element
     rather than hardcoded, so a change to --fs-body-size needs no change here. */
  get clampHeight() {
    const lineHeight = parseFloat(getComputedStyle(this.body).lineHeight);
    return Number.isFinite(lineHeight) ? lineHeight * FS_CLAMP_LINES : 0;
  }

  update() {
    if (this.desktop.matches) {
      this.apply(false, false);
      return;
    }

    const clamp = this.clampHeight;
    // scrollHeight ignores max-height, so this reads the same whether the body
    // is currently clamped or open — no need to unclamp to measure.
    const overflows = clamp > 0 && this.body.scrollHeight > clamp + 1;

    if (!overflows) this.expanded = false;
    this.apply(overflows, overflows && !this.expanded);
  }

  apply(showButton, clamped) {
    this.classList.toggle('fs__body-wrap--clamped', clamped);
    this.button.hidden = !showButton;
    this.button.setAttribute('aria-expanded', String(this.expanded));
    if (this.label) {
      this.label.textContent = this.expanded
        ? this.button.dataset.labelLess
        : this.button.dataset.labelMore;
    }
  }

  toggle() {
    this.expanded = !this.expanded;

    // Opening only adds copy below the cut, so nothing already on screen moves
    // and there is nothing to correct.
    if (this.expanded) {
      this.update();
      return;
    }

    // Closing takes copy out from above the button, so the rest of the page
    // slides up under the reader and the control leaves their thumb. Put the
    // window back by exactly what moved — `window.scrollBy`, not
    // `scrollIntoView`, which would drag every scrollable ancestor with it
    // (failsafe Topic 12).
    const before = this.button.getBoundingClientRect().top;
    this.update();
    const after = this.button.getBoundingClientRect().top;
    if (after !== before) window.scrollBy(0, after - before);
  }
}

if (!customElements.get('feature-story-body')) {
  customElements.define('feature-story-body', FeatureStoryBody);
}
